/**
 * PurchaseOrderLinesCard — the PO line-items panel rendered inside the resource
 * detail drawer as the resource's only form-rail tab (#1420, #1804).
 *
 * The composite `{ purchase_order, items }` GET is flattened by the resource's
 * `api.detail` into ONE detail record (`{ ...purchase_order, items }`), so the
 * framework header AND these lines both come from the SINGLE detail fetch — no
 * second GET per drawer open. This card is therefore purely presentational: it
 * receives `items` as a prop off the already-fetched record. `items` is
 * `undefined` only while the detail query is still resolving (the framework
 * shows the list-row snapshot, which carries no lines) → a loading placeholder.
 */
import { useTranslation } from 'react-i18next';
import { Loading } from '@resource-kit/ui/react';
import { formatNumber } from '@lib/utils';
import { parseDecimalText } from '@/services';
import type { PurchaseOrderItem } from '@/services';

export function PurchaseOrderLinesCard({ items }: { items: PurchaseOrderItem[] | undefined }) {
  const { t } = useTranslation();

  // The detail fetch has not resolved yet (the panel is showing the list-row
  // snapshot, which has no `items`). The framework owns the detail query's
  // loading/error surfaces; this card only bridges the resolved lines.
  if (items === undefined) return <Loading />;

  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-2" data-testid="po-lines-empty">
        {t('procurement.purchaseOrders.noLines')}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="po-lines">
      <div className="text-xs font-medium uppercase text-muted-foreground">
        {t('procurement.purchaseOrders.linesTitle')}
      </div>
      <table className="w-full text-sm border-t">
        <thead>
          <tr className="text-left text-xs uppercase text-muted-foreground">
            <th className="py-2">{t('procurement.purchaseOrders.col.ingredient')}</th>
            <th className="py-2 text-right">{t('procurement.purchaseOrders.col.qtyOrdered')}</th>
            <th className="py-2 text-right">{t('procurement.purchaseOrders.col.qtyReceived')}</th>
            <th className="py-2">{t('procurement.purchaseOrders.col.unit')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-t">
              <td className="py-2">{item.ingredient_id}</td>
              <td className="py-2 text-right tabular-nums">
                {formatNumber(
                  parseDecimalText(
                    item.qty_ordered,
                    `PurchaseOrderLinesCard:${item.id}:qty_ordered`,
                  ),
                  { maximumFractionDigits: 3 },
                )}
              </td>
              <td className="py-2 text-right tabular-nums">
                {formatNumber(
                  parseDecimalText(
                    item.qty_received,
                    `PurchaseOrderLinesCard:${item.id}:qty_received`,
                  ),
                  { maximumFractionDigits: 3 },
                )}
              </td>
              <td className="py-2 text-muted-foreground">{item.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
